import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as LocalShippingIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { usePharmacy } from '../../hooks/usePharmacy';

/**
 * Componente de Gestión de Inventario - Segunda pestaña
 * Control de stock, lotes y ubicaciones
 */
const InventoryManagement = () => {
  const { searchProducts, getProductStock, transferStock, loading, error } = usePharmacy();
  
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [transferForm, setTransferForm] = useState({
    quantity: '',
    fromLocation: '',
    toLocation: '',
    reason: ''
  });

  const locations = [
    { id: 'farmacia-central', name: 'Farmacia Central' },
    { id: 'pabellon-1', name: 'Pabellón 1' },
    { id: 'pabellon-2', name: 'Pabellón 2' },
    { id: 'carrito-anestesia-1', name: 'Carrito Anestesia 1' },
    { id: 'hospitalizacion', name: 'Hospitalización' }
  ];

  // Cargar productos
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const allProducts = await searchProducts('');
      const productsWithStock = await Promise.all(
        allProducts.map(async (product) => {
          const stock = await getProductStock(product.id);
          return {
            ...product,
            totalStock: stock.totalStock,
            locations: stock.locations,
            batches: stock.batches
          };
        })
      );
      setProducts(productsWithStock);
      setFilteredProducts(productsWithStock);
    } catch (err) {
      console.error('Error cargando productos:', err);
    }
  };

  // Filtrar productos
  useEffect(() => {
    let filtered = products;

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.genericName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por ubicación
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(product =>
        product.locations?.includes(selectedLocation)
      );
    }

    setFilteredProducts(filtered);
  }, [searchTerm, selectedLocation, products]);

  const handleTransfer = async () => {
    if (!selectedProduct || !transferForm.quantity || !transferForm.fromLocation || !transferForm.toLocation) {
      return;
    }

    try {
      await transferStock({
        productId: selectedProduct.id,
        batchId: selectedProduct.batches[0]?.id,
        quantity: parseFloat(transferForm.quantity),
        fromLocation: transferForm.fromLocation,
        toLocation: transferForm.toLocation,
        reason: transferForm.reason,
        transferredBy: 'current-user-id'
      });

      setShowTransferDialog(false);
      setTransferForm({ quantity: '', fromLocation: '', toLocation: '', reason: '' });
      setSelectedProduct(null);
      loadProducts();
    } catch (err) {
      console.error('Error en transferencia:', err);
    }
  };

  const getStockStatus = (product) => {
    if (product.totalStock === 0) return { label: 'Sin Stock', color: 'error' };
    if (product.totalStock <= product.minStockLevel) return { label: 'Stock Bajo', color: 'warning' };
    return { label: 'Disponible', color: 'success' };
  };

  const renderInventoryTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell>Presentación</TableCell>
              <TableCell align="center">Stock Total</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Ubicaciones</TableCell>
              <TableCell align="center">Próximo Vencimiento</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => {
              const stockStatus = getStockStatus(product);
              const nextExpiring = product.batches?.find(b => b.isActive)?.expirationDate;
              
              return (
                <TableRow key={product.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {product.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {product.genericName}
                      </Typography>
                      {product.isControlled && (
                        <Chip label="Controlado" size="small" color="error" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{product.presentation}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold">
                      {product.totalStock} {product.unitOfMeasure}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={stockStatus.label}
                      color={stockStatus.color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {product.locations?.map((loc) => (
                        <Chip
                          key={loc}
                          label={locations.find(l => l.id === loc)?.name || loc}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {nextExpiring ? (
                      <Typography variant="body2">
                        {new Date(nextExpiring.seconds * 1000).toLocaleDateString()}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Ver detalles">
                        <IconButton size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Transferir stock">
                        <IconButton 
                          size="small"
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowTransferDialog(true);
                          }}
                        >
                          <LocalShippingIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          <TrendingUpIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Gestión de Inventario
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Control de stock, lotes y ubicaciones de medicamentos e insumos
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {/* Filtros y acciones */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <TextField
              fullWidth
              label="Buscar producto"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel>Ubicación</InputLabel>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <MenuItem value="all">Todas las ubicaciones</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 5
            }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<AddIcon />}>
                Nuevo Producto
              </Button>
              <Button variant="outlined">
                Exportar Excel
              </Button>
              <Button variant="outlined">
                Actualizar Stock
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      {/* Tabla de inventario */}
      <Paper sx={{ p: 2 }}>
        {renderInventoryTable()}
      </Paper>
      {/* Diálogo de transferencia */}
      <Dialog open={showTransferDialog} onClose={() => setShowTransferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transferir Stock</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Producto: <strong>{selectedProduct.name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stock actual: <strong>{selectedProduct.totalStock} {selectedProduct.unitOfMeasure}</strong>
              </Typography>
            </Box>
          )}
          
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Cantidad a transferir"
                type="number"
                value={transferForm.quantity}
                onChange={(e) => setTransferForm({...transferForm, quantity: e.target.value})}
              />
            </Grid>
            
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Desde</InputLabel>
                <Select
                  value={transferForm.fromLocation}
                  onChange={(e) => setTransferForm({...transferForm, fromLocation: e.target.value})}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Hacia</InputLabel>
                <Select
                  value={transferForm.toLocation}
                  onChange={(e) => setTransferForm({...transferForm, toLocation: e.target.value})}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={12}>
              <TextField
                fullWidth
                label="Motivo de transferencia"
                multiline
                rows={2}
                value={transferForm.reason}
                onChange={(e) => setTransferForm({...transferForm, reason: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTransferDialog(false)}>Cancelar</Button>
          <Button onClick={handleTransfer} variant="contained">
            Transferir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryManagement;
